import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  IconButton,
  MobileStepper,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';

const steps = [
  {
    title: 'Welcome to the NLFX Professional Cable Builder!',
    content: 'Design your perfect custom cable in just a few easy steps. Follow the guided process to create exactly what you need.'
  },
  {
    title: 'Step 1: Choose Cable Type',
            content: 'Select from four main cable types: Audio (Microphone, Instrument, Speaker), Power, DMX (lighting control), or Hybrid (combines multiple signal types). Each type shows compatible connectors and use cases.'
  },
  {
    title: 'Step 2: Select Style',
    content: 'Choose your cable style: End-to-End (standard cable), Hybrid (combines different signal types), Zip (multiple cables in one jacket), or Wye (splits from one to multiple connectors).'
  },
  {
    title: 'Step 3: Configure Connectors',
    content: 'Select connectors for each end: Choose connector types (XLR, TRS, Speakon, etc.), select gender and mounting options as available. Only compatible connectors appear for your cable type.'
  },
  {
    title: 'Step 4: Set Length',
    content: 'Enter your desired cable length in feet. The system shows minimum/maximum lengths and calculates pricing automatically.'
  },
  {
    title: 'Step 5: Finishing Options',
    content: 'Finishing: NLFX branding appears at every connector by default. You can add optional 2" heat-shrink tubing farther up the cable from each plug (on the jacket) — clear unshrunk tubing for your own labels before shrinking, or custom printed options in the full builder.'
  },
  {
    title: 'Step 6: Review & Order',
    content: 'Review your complete configuration, see the final price and SKU, then add to your cart. Your cable will be built to your exact specifications.'
  },
  {
    title: 'Getting Help & Saving Configs',
    content: 'Use the "Need Help?" menu for Quick Start Guide, FAQ, and Live Chat support. Use the "Configs" menu to save configurations, access your saved cables, or browse public templates.'
  },
  {
    title: 'Navigation Tips',
    content: 'The progress stepper shows your current step. Use Back/Next buttons to navigate, or click "Reset" to start over. Your cart icon shows saved items and you can review them anytime.'
  }
];

export default function QuickStartGuideDialog({ open, onClose }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (open) {
      // Always start from the beginning when opened
      setActiveStep(0);
    }
  }, [open]);



  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };
  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };
  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullScreen={fullScreen} maxWidth="sm" fullWidth>
      <DialogTitle>
        {steps[activeStep].title}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ minHeight: 120, py: 2 }}>{steps[activeStep].content}</Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleBack} disabled={activeStep === 0}>Back</Button>
        {activeStep === steps.length - 1 ? (
          <Button onClick={handleClose}>Close</Button>
        ) : (
          <Button onClick={handleNext} disabled={activeStep === steps.length - 1}>Next</Button>
        )}
      </DialogActions>
      <MobileStepper
        variant="dots"
        steps={steps.length}
        position="static"
        activeStep={activeStep}
        nextButton={null}
        backButton={null}
        sx={{ justifyContent: 'center', pb: 1 }}
      />
    </Dialog>
  );
} 